import { useState } from 'react';
import { InlineWidget } from 'react-calendly';
import { Mail } from 'lucide-react';
import { OnboardingLayout, useSlideSetup } from '@/features/client-onboarding';
import { Button } from '@design-system';
import { RequestAdvisorMeetingModal } from '@/components/RequestAdvisorMeetingModal';
import { useNavigateToPage } from '@/Router';
import { OnboardingD2CPaywall } from '@/pages/OnboardingD2CPaywall';

const CALENDLY_URL = 'https://calendly.com/lily-waterlily/waterlily-new-user-walkthrough';
const ADAM_SHELL_PHOTO = '/adam_shell.avif';

export function OnboardingD2CVirtualMeeting({ clientId }: ClientIdProps) {
  const navigateToPage = useNavigateToPage();
  const [showMeetingModal, setShowMeetingModal] = useState(false);

  useSlideSetup({
    slidePage: OnboardingD2CVirtualMeeting,
    nextButtonLabel: '',
    shouldHideNextNavigation: true,
    onBackClick: () => navigateToPage(OnboardingD2CPaywall, { clientId }),
  });

  return (
    <OnboardingLayout>
      <div className="mx-auto w-full max-w-6xl px-4 py-6 md:px-0 md:pb-16">
        <div className="flex flex-col gap-6 lg:grid lg:grid-cols-2 lg:gap-x-8">
          <div className="order-2 lg:order-1">
            <h1 className="text-primary text-2xl font-bold md:text-3xl lg:text-4xl">
              Build your plan
            </h1>
            <p className="mt-2 text-sm font-semibold italic text-gray-900 underline">
              A free 45-minute session to create your long-term care plan.
            </p>

            <div className="mt-4 space-y-4 text-sm leading-relaxed text-gray-700 md:mt-6 md:text-base">
              <p>
                This is your chance to meet with Lily, the CEO of Waterlily, a 2x invited World
                Economic Forum speaker and bleeding edge thought leader in the long-term care
                industry, <strong>for free.</strong>
              </p>
              <p>
                How is it free? Just 5 times per week, Lily meets with families as part of our
                company mission and to ensure she always has a pulse on what matters most, so we can
                make Waterlily more effective for families like your own.
              </p>
              <p>
                Book time to build a long-term care plan for yourself or a loved one using the
                scheduler <span className="hidden lg:inline">on the right</span>
                <span className="lg:hidden">above</span>.
              </p>

              <div className="mt-4 rounded-2xl bg-gray-50 p-4 md:mt-6 md:p-5">
                <p className="italic text-gray-700">
                  &ldquo;In just a few minutes, I was able to determine the age at which my care
                  would begin, how long it would last and its cost. The system also provided a
                  funding plan to cover the costs.&rdquo;
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <img
                    src={ADAM_SHELL_PHOTO}
                    alt="Adam Shell"
                    className="size-10 rounded-full object-cover md:size-12"
                  />
                  <div>
                    <p className="text-sm font-bold text-gray-900">Adam Shell</p>
                    <p className="text-xs text-gray-600">
                      Journalist for USA Today, Investor&apos;s Business Daily, Kiplinger and more.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 lg:row-start-1 lg:col-start-2 lg:row-span-2">
            <InlineWidget
              url={CALENDLY_URL}
              styles={{ minWidth: '280px', height: '660px' }}
            />
          </div>
        </div>

        <div className="mt-8 rounded-2xl bg-gray-50 p-4 text-center md:p-6 lg:ml-auto lg:max-w-xl">
          <p className="text-sm leading-relaxed text-gray-700">
            Don&apos;t see any appointments near enough to when you&apos;d like to plan? Click here
            to contact us with your LTC planning need and we&apos;ll see if we can escalate your
            request:
          </p>
          <Button
            size="lg"
            className="mt-4 w-full rounded-full"
            onClick={() => setShowMeetingModal(true)}
          >
            <Mail className="size-4" />
            Contact Us
          </Button>
        </div>
      </div>

      <RequestAdvisorMeetingModal
        open={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        bodyPrefix="Virtual Meeting Scheduling Request"
      />
    </OnboardingLayout>
  );
}
